import React from "react";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { commitLocalUpdate, graphql, type RecordProxy } from "relay-runtime";

import { useSyncedPersistentState } from "~/actionItems/hooks/useSyncedPersistentState";
import type { CreateOrImportProps } from "~/integrations/actionItemIntegration/actionItemLinkingModal";
import { base64Encode } from "~/lib/base64";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Input } from "~/ui/legacyFellowIndex";
import Tippy from "~/ui/Tooltip";

import { BoardSelector } from "./selectors/TrelloBoardSelector";
import { ListSelector } from "./selectors/TrelloListSelector";
import { OrganizationSelector } from "./selectors/TrelloOrganizationSelector";
import { trelloStore } from "./store";

import type { CreateNewExternalTicketPaneInTrelloMutation as CreateMutation } from "~/__generated__/CreateNewExternalTicketPaneInTrelloMutation.graphql";
import type { TrelloBoardSelectorQuery } from "~/__generated__/TrelloBoardSelectorQuery.graphql";

import styles from "./css/styles.module.css";

type BoardNode = NonNullable<
	NonNullable<TrelloBoardSelectorQuery["response"]["viewer"]["integrations"]["trello"]>["boards"]
>[0];

const createExternalTicket = graphql`
	mutation CreateNewExternalTicketPaneInTrelloMutation(
		$blockKey: String!
		$noteId: ID!
		$title: String!
		$destinationInfo: JSON!
	) {
		sendActionItemToIntegration(
			input: {
				blockKey: $blockKey
				noteId: $noteId
				title: $title
				destinationInfo: $destinationInfo
				integrationProvider: TRELLO
			}
		) {
			clientMutationId
		}
	}
`;

function link(
	blockKey: string,
	noteId: string,
	title: string,
	boardId: string,
	listId: string,
	uncheckedListId: string | undefined,
	doneListId: string | undefined,
) {
	return new Promise<boolean>(resolve => {
		commitLocalUpdate(environment, store => {
			const relayId = base64Encode(`TrelloBoardNode:${boardId}`);
			const boardNode = store.get(relayId) as RecordProxy<BoardNode>;
			commitMutationPromise<CreateMutation>(environment, {
				mutation: createExternalTicket,
				variables: {
					blockKey,
					noteId,
					title,
					/* eslint-disable camelcase */
					destinationInfo: {
						list_id: listId,
						board_id: boardId,
						board_name: boardNode?.getValue("name"),
						unchecked_list_id: uncheckedListId,
						done_list_id: doneListId,
					},
					/* eslint-enable camelcase */
				},
				onCompleted: () => {
					resolve(true);
				},
				onError: () => {
					resolve(false);
				},
			});
		});
	});
}

export function CreateNewExternalTicketPane({
	setCanSubmit,
	registerSubmit,
	initialText,
	blockKey,
	noteId,
}: CreateOrImportProps) {
	const [organizationId, setOrganizationId] = useSyncedPersistentState<string | undefined>(
		trelloStore,
		"organizationId",
		undefined,
	);
	const [boardId, setBoardId] = useSyncedPersistentState<string | undefined>(trelloStore, "boardId", undefined);
	const [listId, setListId] = useSyncedPersistentState<string | undefined>(trelloStore, "listId", undefined);
	const [uncheckedListId, setUncheckedListId] = useSyncedPersistentState<string | undefined>(
		trelloStore,
		"uncheckedListId",
		undefined,
	);
	const [doneListId, setDoneListId] = useSyncedPersistentState<string | undefined>(
		trelloStore,
		"doneListId",
		undefined,
	);
	const [title, setTitle] = React.useState<string | undefined>(initialText ? initialText : undefined);
	const [displayListsError, setDisplayListsError] = React.useState(false);

	const onTitleChange = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
		// @ts-ignore
		setTitle(e.target.value);
	}, []);

	const submit = React.useCallback(async () => {
		if (!title || !listId || !boardId) {
			throw new Error("Submission validation step failed");
		}

		return link(blockKey, noteId, title, boardId, listId, uncheckedListId, doneListId);
	}, [blockKey, noteId, title, listId, boardId, uncheckedListId, doneListId]);

	React.useEffect(() => {
		registerSubmit(submit);
	}, [registerSubmit, submit]);

	React.useEffect(() => {
		const mainFieldsFilled = Boolean(organizationId && boardId && listId && title);
		const extraFieldsFilled =
			Boolean(uncheckedListId && doneListId && uncheckedListId != doneListId) ||
			Boolean(!uncheckedListId && !doneListId);

		setCanSubmit(mainFieldsFilled && extraFieldsFilled);
		setDisplayListsError(Boolean(uncheckedListId && doneListId && uncheckedListId == doneListId));
	}, [setCanSubmit, organizationId, boardId, listId, title, uncheckedListId, doneListId]);

	return (
		<div className={styles.gridContainer}>
			<div className={styles.organization}>
				<div className={styles.label}>Organization</div>
				<OrganizationSelector organizationId={organizationId} setOrganizationId={setOrganizationId} />
			</div>
			<div className={styles.board}>
				<div className={styles.label}>Board</div>
				<BoardSelector organizationId={organizationId} boardId={boardId} setBoardId={setBoardId} />
			</div>
			<div className={styles.list}>
				<div className={styles.label}>
					List
					<Tippy
						placement="top"
						className={styles.toolTip}
						content={"When you create this item, select the list it will sync to in Trello"}
					>
						<span className={styles.helpPopup}>
							<FontAwesomeIcon icon={faInfoCircle} />
						</span>
					</Tippy>
				</div>
				<ListSelector boardId={boardId} listId={listId} setListId={setListId} />
			</div>
			<div className={styles.unchecked}>
				<div className={styles.label}>
					Unchecked list
					<Tippy
						placement="top"
						className={styles.toolTip}
						content={"When you uncheck this item, select the list it will be moved to in Trello"}
					>
						<span className={styles.helpPopup}>
							<FontAwesomeIcon icon={faInfoCircle} />
						</span>
					</Tippy>
				</div>
				<ListSelector boardId={boardId} listId={uncheckedListId} setListId={setUncheckedListId} />
			</div>
			<div className={styles.done}>
				<div className={styles.label}>
					Done list
					<Tippy
						placement="top"
						className={styles.toolTip}
						content={"When you check this item, select the list it will be moved to in Trello"}
					>
						<span className={styles.helpPopup}>
							<FontAwesomeIcon icon={faInfoCircle} />
						</span>
					</Tippy>
				</div>
				<ListSelector boardId={boardId} listId={doneListId} setListId={setDoneListId} />
			</div>
			<div className={styles.card}>
				<div className={styles.label}>Card name</div>
				<Input
					name="trello_card_name"
					className={styles.titleInput}
					type="text"
					placeholder="Set a title for this card"
					value={title}
					onChange={onTitleChange}
				/>
			</div>
			<div className={styles.help}>
				{displayListsError && (
					<div className={styles.error}>You cannot specify the same list for unchecked and done</div>
				)}

				<div>The card name, due date, and assignee will automatically sync with Trello.</div>
			</div>
		</div>
	);
}
